/**
 * Checks if content is empty or contains only empty HTML tags
 * @param content - The content to check
 * @returns true if content is empty, false otherwise
 */
export const isEmptyContent = (content: string): boolean => {
  if (!content || typeof content !== 'string') {
    return true;
  }
  
  const trimmed = content.trim();
  
  // Common empty HTML patterns from rich text editors
  const emptyPatterns = [
    '<p></p>',
    '<p><br></p>',
    '<p><br/></p>',
    '<div></div>',
    '<div><br></div>',
    '<div><br/></div>',
    '<span></span>',
    '&nbsp;',
    '\u00A0', // non-breaking space
    ''
  ];
  
  // If it matches empty patterns, it's empty
  if (emptyPatterns.includes(trimmed)) {
    return true;
  }
  
  // Check if content has actual text content (not just HTML tags)
  // Remove HTML tags and check if there's meaningful content
  const textContent = trimmed.replace(/<[^>]*>/g, '').trim();
  
  // If there's no text content after removing HTML tags, it's empty
  const isEmpty = textContent === '';
  return isEmpty;
};

/**
 * Cleans content by removing empty HTML tags and converting to empty string if truly empty
 * @param content - The content to clean
 * @returns Cleaned content or empty string
 */
export const cleanContent = (content: string): string => {
  const isEmpty = isEmptyContent(content);
  if (isEmpty) {
    return '';
  }
  return content;
};

/**
 * Validates if content is not empty for form validation
 * @param content - The content to validate
 * @returns true if content is not empty, false otherwise
 */
export const isContentValid = (content: string): boolean => {
  return !isEmptyContent(content);
};
